import { MouseEvent, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Input, notification, Typography } from 'antd'
import dayjs from 'dayjs'
import useMutation from 'hooks/useMutation'
import { CurrentUser } from 'models/User'
import { useRecoilState, useRecoilValue } from 'recoil'
import {
  buildingFilesState,
  buildingModeState,
  listPointState,
  revalidatePointDetailState,
  selectedPoint,
  selectedPointDetail,
  selectedSubImageState
} from 'store/buildingStore'
import { currentOpenMenu } from 'store/displayToggleStore'
import { BuildingFile } from 'types/building'
import { getSafeThumbnailUrl } from 'utils'
import thumbnailQueue from 'utils/thumbnailQueue'

import LazyThumbnail, { ImageType } from 'components/atoms/LazyThumbnail'
import Spinner from 'components/atoms/Spinner'
import DeleteIcon from 'components/icons/DeleteIcon'
import EditIcon from 'components/icons/EditIcon'

import ModalSetInitViewSubImage from './ModalSetInitViewSubImage'

type Props = {
  isSelected: boolean
  subImage: BuildingFile
  handleSelectSubImage: (file: BuildingFile) => void
  profile: CurrentUser | null
}

export default function PointSubImage({
  isSelected,
  subImage,
  handleSelectSubImage,
  profile
}: Props) {
  const { id: buildingId, floorId } = useParams()
  const { t } = useTranslation()
  const [openModalSetInitView, setOpenModalSetInitView] =
    useState<boolean>(false)
  const [, setPointList] = useRecoilState(listPointState)
  const [selectedSubImage, setSelectedSubImage] = useRecoilState(
    selectedSubImageState
  )
  const [, setBuildingFiles] = useRecoilState(buildingFilesState)
  const [, setRevalidatePointDetail] = useRecoilState(
    revalidatePointDetailState
  )
  const [currentSelectedPoint] = useRecoilState(selectedPoint)
  const [pointDetail, setPointDetail] = useRecoilState(selectedPointDetail)
  const { isOpenRightMenu } = useRecoilValue(currentOpenMenu)

  const [isEditingNote, setIsEditingNote] = useState(false)
  const [newNote, setNewNote] = useState(subImage?.note || '')
  const [isGeneratingThumbnail, setIsGeneratingThumbnail] = useState(false)
  const isMountedRef = useRef(true)
  const [thumbVersion, setThumbVersion] = useState<number>(0)
  const buildingMode = useRecoilValue(buildingModeState)

  const { trigger } = useMutation(
    `v1/points/${currentSelectedPoint}/sub-images`
  )

  const { trigger: triggerGenerateThumbnail } = useMutation(
    `v1/points/${currentSelectedPoint}/generate-thumbnail`
  )

  const { trigger: handleUpdateImageNote } = useMutation(
    `v1/points/${currentSelectedPoint}/images`
  )

  const { trigger: handleDeleteImageNote } = useMutation(
    `v1/points/${currentSelectedPoint}/images`,
    {},
    true
  )

  const saveNoteImage = async () => {
    if (newNote) {
      try {
        await handleUpdateImageNote({
          method: 'patch',
          data: {
            buildingId: Number(buildingId),
            fileId: subImage.id,
            note: newNote
          }
        })
        setIsEditingNote(false)
        setPointDetail((prev) =>
          prev
            ? {
                ...prev,
                subImages: prev.subImages?.map((item) =>
                  item.id === subImage.id ? { ...item, note: newNote } : item
                )
              }
            : prev
        )
      } catch (error) {
        setIsEditingNote(false)
      }
    } else {
      try {
        await handleDeleteImageNote({
          method: 'patch',
          data: {
            buildingId: Number(buildingId),
            fileId: subImage.id,
            note: ''
          }
        })
        setIsEditingNote(false)
        setPointDetail((prev) =>
          prev
            ? {
                ...prev,
                subImages: prev.subImages?.map((item) =>
                  item.id === subImage.id ? { ...item, note: '' } : item
                )
              }
            : prev
        )
      } catch (error) {
        setIsEditingNote(false)
      }
    }
  }

  const removeNoteImage = async () => {
    try {
      await handleDeleteImageNote({
        method: 'patch',
        data: {
          buildingId: Number(buildingId),
          fileId: subImage.id,
          note: ''
        }
      })
      setNewNote('')
    } catch (error) {
      /* eslint-disable no-console  */
    }
  }

  const handleDeleteSubimage = (e: MouseEvent<HTMLDivElement>, id: number) => {
    removeNoteImage()
    e.stopPropagation()
    trigger(
      {
        method: 'delete',
        data: null,
        query: [`${id}`],
        params: {
          building_id: +(buildingId || 0),
          floor_id: +(floorId || 0)
        }
      },
      {
        onSuccess: () => {
          const subImages = pointDetail?.subImages?.filter(
            (item) => item.id !== id
          )
          setPointList((prev) =>
            prev.map((item) =>
              item.id === currentSelectedPoint
                ? { ...item, isSubImages: Boolean(subImages?.length) }
                : item
            )
          )
          isOpenRightMenu &&
            setBuildingFiles((item) => ({
              ...item,
              files: item.files.map((image) =>
                image.id === id ? { ...image, pointId: null } : image
              )
            }))
          id === selectedSubImage && setSelectedSubImage(undefined)
          currentSelectedPoint === pointDetail?.id &&
            setRevalidatePointDetail(currentSelectedPoint)
        }
      }
    )
  }

  const isToggleAble = useMemo(() => {
    return (
      selectedSubImage &&
      pointDetail?.isLocked &&
      pointDetail.lockedBy === profile?.id
    )
  }, [pointDetail, profile?.id, selectedSubImage])

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    setIsEditingNote(false)
    setNewNote(
      pointDetail?.subImages?.find((item) => item.id === subImage.id)?.note ||
        ''
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubImage])

  useEffect(() => {
    if (subImage.needsThumbnail && currentSelectedPoint) {
      thumbnailQueue.enqueue(
        () =>
          new Promise((resolve) => {
            if (isMountedRef.current) {
              setIsGeneratingThumbnail(true)
            }
            triggerGenerateThumbnail(
              {
                method: 'post',
                data: {
                  building_id: Number(buildingId),
                  floor_id: Number(floorId),
                  sub_image_id: subImage.id,
                  init_view: {
                    h: subImage.initView?.h ?? 0,
                    v: subImage.initView?.v ?? 0,
                    z: subImage.initView?.z ?? 80
                  }
                }
              },
              {
                onSuccess: (res: any) => {
                  if (isMountedRef.current) {
                    setIsGeneratingThumbnail(false)
                    setThumbVersion(Date.now())
                  }
                  const newThumbUrl =
                    res?.data?.thumbnail_url || res?.thumbnail_url
                  setPointDetail((prev) => {
                    if (!prev) return prev
                    return {
                      ...prev,
                      subImages: prev.subImages?.map((item) =>
                        item.id === subImage.id
                          ? {
                              ...item,
                              needsThumbnail: false,
                              thumbnailUrl: newThumbUrl || item.thumbnailUrl
                            }
                          : item
                      )
                    }
                  })
                  setPointList((prev) =>
                    prev.map((item) =>
                      item.id === currentSelectedPoint
                        ? {
                            ...item,
                            subImages: item.subImages?.map((sub) =>
                              sub.id === subImage.id
                                ? {
                                    ...sub,
                                    needsThumbnail: false,
                                    thumbnailUrl:
                                      newThumbUrl || sub.thumbnailUrl
                                  }
                                : sub
                            )
                          }
                        : item
                    )
                  )
                  resolve(res)
                },
                onError: () => {
                  if (isMountedRef.current) {
                    setIsGeneratingThumbnail(false)
                  }
                  resolve(null)
                }
              }
            )
          }),
        `sub_${currentSelectedPoint}_${subImage.id}`
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subImage.needsThumbnail, currentSelectedPoint, subImage.initView])

  const handleClickNote = () => {
    const placeHolder =
      pointDetail?.subImages?.find((item) => item.id === subImage.id)?.note ||
      (pointDetail?.subImages?.find((item) => item.id === subImage.id)
        ?.takenAt &&
        dayjs(
          pointDetail?.subImages?.find((item) => item.id === subImage.id)
            ?.takenAt
        )
          .utc()
          .format('YYYY-MM-DD HH:mm')) ||
      ''
    handleSelectSubImage(subImage)
    requestAnimationFrame(() => {
      setNewNote(placeHolder)
      setIsEditingNote(true)
    })
  }

  const handleClickItem = () => {
    if (isGeneratingThumbnail || subImage.needsThumbnail) {
      notification.info({
        message: t(
          'building.generatingNewThumbnail',
          '新しいサムネイルを生成中です'
        )
      })
    }
    handleSelectSubImage(subImage)
  }

  return (
    <div
      className={`min-h-[36px] p-[10px] flex border-0 border-b border-solid theme-border-secondary relative ${
        isSelected ? 'theme-bg-sub-primary' : 'theme-bg-third'
      }`}
      onClick={handleClickItem}
    >
      <div className="relative overflow-hidden flex-shrink-0 w-[220px] h-[110px]">
        <LazyThumbnail
          imageType={ImageType.OBJECT_FILL}
          url={getSafeThumbnailUrl(
            subImage.thumbnailUrl ||
              (subImage.multires
                ? `${subImage.tilesUrl}thumb.jpg`
                : subImage.thumbnail),
            thumbVersion
          )}
          name={subImage.name}
          width={220}
          height={110}
        />
        {isGeneratingThumbnail && (
          <div className="absolute inset-0 flex items-center justify-center bg-[#00000050] z-10">
            <Spinner size="small" />
          </div>
        )}
      </div>

      <div className="absolute top-3 right-3">
        <div
          className="cursor-pointer w-5 h-5 z-20 flex justify-center items-center"
          onClick={(e) => handleDeleteSubimage(e, subImage.id)}
        >
          <DeleteIcon />
        </div>
      </div>
      {isToggleAble && isSelected && buildingMode.previewMode && (
        <div
          className="absolute top-3 left-3 z-20"
          onClick={() => setOpenModalSetInitView(true)}
        >
          <EditIcon className="text-[24px] text-white cursor-pointer hover:opacity-80 duration-150" />
        </div>
      )}
      {openModalSetInitView && (
        <ModalSetInitViewSubImage
          openModalSetInitView={openModalSetInitView}
          setOpenModalSetInitView={setOpenModalSetInitView}
        />
      )}
      {!subImage?.note ? (
        <div className="ant-typography  text-white text-[12px] bg-[#02112070] absolute right-[10px] bottom-[10px]">
          <Typography.Text
            className="text-[12px] text-white"
            onClick={(e) => {
              e.stopPropagation()
              handleClickNote()
            }}
          >
            {dayjs(subImage?.takenAt)
              ?.utc()
              .format('YYYY-MM-DD HH:mm')}
          </Typography.Text>
        </div>
      ) : (
        <div className="ant-typography text-white text-[12px] max-w-[180px] bg-[#02112070] absolute right-[10px] bottom-[10px]">
          <Typography.Text
            className="text-[12px] text-white truncate cursor-pointer"
            onClick={(e) => {
              e.stopPropagation()
              handleClickNote()
            }}
            ellipsis={{ tooltip: subImage?.note }}
          >
            {subImage?.note}
          </Typography.Text>
        </div>
      )}
      {isEditingNote && (
        <div className="absolute bottom-0 right-2 w-[200px]">
          <Input
            className="w-full"
            value={newNote}
            onChange={(e) => {
              setNewNote(e.target.value)
            }}
            onBlur={() => {
              setIsEditingNote(false)
              setNewNote(subImage?.note || '')
            }}
            onPressEnter={saveNoteImage}
            autoFocus
          />
        </div>
      )}
    </div>
  )
}
